<template>
  <div>
    <EmptyBlock
      v-if="(!requirement || !requirement.requestStatus) && !currentStatusProps.showAlert"
      custom-class="mt-0 pa-10"
      data-qa="requirements-noData"
      :text="$t('Tenders.NoData')"/>
    <template v-else>
      <sAlert
        v-if="currentStatusProps.showAlert"
        :type="currentStatusProps.type">
        {{ currentStatusProps.alertText || $t(`AIRequirements.Statuses.${requirement.requestStatus}`) }}
      </sAlert>
      <div
        v-if="currentStatusProps.showRequirements"
        id="ai-requirements-tab">
        <AiRequirementItem
          v-for="(requirementItem, index) in requirementsToShow"
          :key="index"
          :item="requirementItem"
          @item-cancel="changeRequirementActive(requirementItem, false)"
          @item-clip="clipRequirement(requirementItem)"
          @item-delete="deleteRequirement(requirementItem)"
          @item-edit="editRequirement(requirementItem)"
          @item-restore="changeRequirementActive(requirementItem, true)"/>

        <InfinityUpdater
          v-if="needsLazyLoading && hasMoreRequirements"
          :action="loadMoreRequirements"
          :active="!isLoading"
          :loading="isLoading"
          selector="#ai-requirements-tab"/>

        <AiRequirementsAddedEdit
          :item="editModal.item"
          :open.sync="editModal.open"
          :tender="tender"
          @ok="sendChangeRequirement"/>
      </div>
    </template>
  </div>
</template>
<script>
import EmptyBlock from 'components/common/elements/EmptyBlock.vue'
import AiRequirementItem from 'components/tenders/tender page/tender tabs/AiRequirementItem.vue'
import AiRequirementsAddedEdit from 'components/tenders/tender page/tender tabs/AiRequirementsAddedEdit.vue'
import InfinityUpdater from 'components/extends/InfinityUpdater.vue'

import AiApi from '@/api/aiApi'
const aiApi = new AiApi()

export default {
  name: 'AiRequirementsTab',
  data() {
    return {
      editModal: {
        item: null,
        open: false
      },
      requestStatuProps: {
        NOT_STARTED: {
          type: 'info',
          alertText: this.$t('AIRequirements.StatusesText.NOT_STARTED'),
          showAlert: true,
          showRequirements: false
        },
        UNDEFINED: {
          type: 'info',
          showAlert: false,
          showRequirements: false
        },
        IN_PROGRESS: {
          type: 'info',
          alertText: this.$t('AIRequirements.StatusesText.IN_PROGRESS'),
          showAlert: true,
          showRequirements: false
        },
        COMPLETED: {
          showAlert: false,
          showRequirements: true
        },
        ERROR: {
          type: 'error',
          showAlert: true,
          alertText: this.$t('AIRequirements.StatusesText.ERROR'),
          showRequirements: false
        },
        REQUIREMENTS_FOUND: {
          showAlert: false,
          showRequirements: true
        },
        RELATIVE_DOCUMENTS_FOUND: {
          showAlert: false,
          showRequirements: true
        },
      },
      // Ленивая загрузка
      displayedRequirements: [],
      itemsPerLoad: 10,
      currentlyLoaded: 0,
      isLoading: false
    }
  },
  methods: {
    showErrorApi(error) {
      this.$notice.error({
        title: this.$t('Common.ErrorTitle'),
        message: error || this.$t('Errors.ServerError'),
        showClose: true,
        progress: true,
      })
    },
    editRequirement(item) {
      this.editModal.item = this.$_.cloneDeep(item)
      this.editModal.open = true
    },
    updateRequirementInList(id, updatedRequirement) {
      const result = this.$_.cloneDeep(this.requirementMutable)
      const reqIndex = result.requirements.findIndex(req => req.id === id)
      if (reqIndex !== -1) {
        this.$set(result.requirements, reqIndex, updatedRequirement)
        this.requirementMutable = result
      }
    },

    sendChangeRequirement(updatedItem) {
      this.$set(updatedItem, 'loading', true)
      const model = {
        requirementId: updatedItem.id,
        title: updatedItem.title,
        contents: updatedItem.contents
      }
      aiApi.editRequirement(model)
        .then((resultApi) => {
          if (!resultApi) {
            this.showErrorApi()
            return
          }
          this.updateRequirementInList(updatedItem.id, resultApi)
          this.$notice.info({
            message: this.$t('AIRequirements.RequirementUpdatedSuccessfully'),
            duration: 10
          })
        })
        .finally(() => {
          this.$set(updatedItem, 'loading', false)
        })
        .catch(() => {
          this.showErrorApi(this.$t('AIRequirements.RequirementUpdatedError'))
        })
    },

    changeRequirementActive(item, active) {
      this.$set(item, 'loading', true)
      aiApi.editRequirementActive(item.id, active)
        .then((resultApi) => {
          if (!resultApi) {
            this.showErrorApi()
            return
          }
          this.updateRequirementInList(resultApi.id, resultApi)
        })
        .finally(() => {
          this.$set(item, 'loading', false)
        })
        .catch(() => {
          this.showErrorApi(active ? this.$t('AIRequirements.RequirementActivateError') : this.$t('AIRequirements.RequirementDeactivateError'))
        })
    },
    clipRequirement(item) {
      this.$set(item, 'loading', true)
      this.$emit('item-clip', item)
      this.$set(item, 'loading', false)
    },
    deleteRequirement(item) {
      this.$set(item, 'loading', true)
      this.$emit('item-delete', item)
      this.$set(item, 'loading', false)
    },
    
    // Инициализация ленивой загрузки
    initLazyLoading() {
      this.displayedRequirements = []
      this.currentlyLoaded = 0
      this.loadMoreRequirements()
    },

    // Загрузка следующей порции требований
    async loadMoreRequirements() {
      if (this.isLoading || !this.requirementMutable?.requirements) {
        return
      }

      this.isLoading = true
      
      try {
        const nextBatch = this.requirementMutable.requirements.slice(
          this.currentlyLoaded,
          this.currentlyLoaded + this.itemsPerLoad
        )
        
        // Имитируем небольшую задержку для лучшего UX
        await new Promise(resolve => setTimeout(resolve, 100))
        
        this.displayedRequirements.push(...nextBatch)
        this.currentlyLoaded += nextBatch.length
      } catch (error) {
        this.$serror('Ошибка при загрузке требований:', error)
      } finally {
        this.isLoading = false
      }
    }
  },
  computed: {
    currentStatusProps() {
      return this.requestStatuProps[this.requirementMutable?.requestStatus] || this.requestStatuProps.UNDEFINED
    },
    needsLazyLoading() {
      return this.requirementMutable?.requirements?.length > 50
    },
    hasMoreRequirements() {
      return this.currentlyLoaded < (this.requirementMutable?.requirements?.length || 0)
    },
    requirementsToShow() {
      if (this.needsLazyLoading) {
        return this.displayedRequirements
      }
      return this.requirementMutable?.requirements || []
    },
    requirementMutable: {
      get() {
        return this.requirement || {}
      },
      set(value) {
        this.$emit('update:requirement', value)
      }
    }
  },
  components: {
    EmptyBlock,
    AiRequirementItem,
    AiRequirementsAddedEdit,
    InfinityUpdater
  },
  created() {
  },
  watch: {
    'requirement.requirements': {
      immediate: true,
      handler(newRequirements) {
        if (newRequirements?.length > 50) {
          this.initLazyLoading()
        } else if (newRequirements?.length) {
          this.displayedRequirements = [...newRequirements]
          this.currentlyLoaded = newRequirements.length
        }
      }
    }
  },
  props: {
    tender: {
      type: Object,
      required: true
    },
    requirement: {
      type: Object,
    }
  },
}
</script>
<style scoped>
</style>
